import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Input, Space, Tag, Typography, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useApp } from '../context/AppContext';
import { Role, View } from '../constants/roles';
import { useApiToken } from '../hooks/useApiToken';
import { enrichCompany, listProviders } from '../utils/integrationsApi';

const { Text } = Typography;

/**
 * Company Onboarding auto-fetch from the Lead Advisor's own company-data provider
 * (POST /me/integrations/enrichment/company, backend e912b97). Workflow stage 1 (profile +
 * enrichment, SPOC review) / Commandment IV spirit: the server returns SUGGESTIONS with their
 * source and never writes them; each field is applied only by an explicit "Apply", and the
 * parent's `onApply` goes through the normal profile edit path (marks the field pending review).
 *
 * Lead Advisor only (the endpoint decrypts the LA's own key). The provider id comes from the
 * server's catalogue (`GET /me/integrations/providers`, kind COMPANY_ENRICHMENT), never hardcoded.
 *
 * `fieldLabels` maps a suggestion `field` to this profile's attribute label; suggestions whose
 * field has no profile attribute are shown for reference but cannot be applied.
 */
const STATE_COPY = {
    NO_MATCH: ['info', 'No match', 'The provider found no company for that domain or name. Check the spelling or try the other field.'],
    CREDENTIAL_REJECTED: ['error', 'The provider rejected your API key', 'Replace the key in Integrations, then try again.'],
    PROVIDER_ERROR: ['error', 'The provider could not be reached or returned an error', 'Nothing was fetched. Try again shortly.'],
};

// Title Case for suggestion fields that have no profile attribute (e.g. `industry`), so reference-only
// rows read like the mapped ones ("Industry", not the raw field name).
const titleCaseField = (f) => String(f).replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').replace(/(^|\s)\w/g, (c) => c.toUpperCase());

// Headcount arrives from the server as a band ("10001+", "201-500"), never an exact count. The profile
// field is free text, so a band is stored faithfully as a band; it must not replace an exact count that
// already lies inside it (that would lose precision), and we never turn a band into a number.
const parseBand = (v) => {
    const m = /^\s*(\d[\d,]*)\s*(\+|-\s*(\d[\d,]*))?\s*$/.exec(String(v));
    if (!m || !m[2]) return null;
    const lo = Number(m[1].replace(/,/g, ''));
    return { lo, hi: m[3] ? Number(m[3].replace(/,/g, '')) : Infinity };
};
const parseExact = (v) => (/^\d[\d,]*$/.test(String(v).trim()) ? Number(String(v).trim().replace(/,/g, '')) : null);

const CompanyEnrichmentPanel = ({ initialName = '', initialDomain = '', fieldLabels, currentValue, canApply, onApply }) => {
    const { user, setCurrentView } = useApp();
    const getToken = useApiToken();
    const isLeadAdvisor = user?.role === Role.LEAD_ADVISOR;

    const [provider, setProvider] = useState({ phase: 'loading', spec: null });
    const [name, setName] = useState(initialName);
    const [domain, setDomain] = useState(initialDomain);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [failure, setFailure] = useState(null);
    const [applied, setApplied] = useState({});

    useEffect(() => {
        if (!isLeadAdvisor) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const providers = await listProviders(getToken);
                const spec = providers.find((p) => p.kind === 'COMPANY_ENRICHMENT') || null;
                if (!cancelled) setProvider({ phase: 'ready', spec });
            } catch (err) {
                if (!cancelled) setProvider({ phase: 'error', spec: null, error: err.message });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isLeadAdvisor, getToken]);

    if (!isLeadAdvisor) return null;
    if (provider.phase === 'loading') return null;

    if (provider.phase === 'error') {
        return <Alert type="warning" showIcon message="Company data lookup is unavailable" description={provider.error || 'Could not load integrations.'} />;
    }
    if (!provider.spec) return null; // server offers no enrichment provider: nothing honest to show

    const openIntegrations = () => setCurrentView(View.INTEGRATIONS);

    const lookup = async () => {
        if (!domain.trim() && !name.trim()) {
            message.warning('Enter a website domain or a company name.');
            return;
        }
        setBusy(true);
        setFailure(null);
        setResult(null);
        setApplied({});
        try {
            const data = await enrichCompany(getToken, { provider: provider.spec.providerId, domain: domain.trim(), name: name.trim() });
            setResult(data);
        } catch (err) {
            setFailure(err.status === 429 ? `Too many lookups. ${err.message}` : err.message || 'Lookup failed.');
        } finally {
            setBusy(false);
        }
    };

    const apply = (s) => {
        onApply(s.field, s.value);
        setApplied((a) => ({ ...a, [s.field]: true }));
    };

    let body = null;
    if (failure) {
        body = <Alert type="error" showIcon message="Lookup failed" description={failure} />;
    } else if (result?.state === 'NO_CREDENTIAL') {
        body = (
            <Alert
                type="warning"
                showIcon
                message={`No ${provider.spec.displayName} key configured`}
                description="Add your own API key in Integrations to look up company data."
                action={<Button size="small" onClick={openIntegrations}>Open Integrations</Button>}
            />
        );
    } else if (result?.state === 'PROVIDER_RATE_LIMITED') {
        body = (
            <Alert
                type="warning"
                showIcon
                message={`${provider.spec.displayName} rate limit reached`}
                description={`Free plans have a small quota.${result.retryAfterSec ? ` Try again in about ${result.retryAfterSec} seconds.` : ' Try again later.'}`}
            />
        );
    } else if (result && STATE_COPY[result.state]) {
        const [type, title, desc] = STATE_COPY[result.state];
        body = (
            <Alert
                type={type}
                showIcon
                message={title}
                description={desc}
                action={result.state === 'CREDENTIAL_REJECTED' ? <Button size="small" onClick={openIntegrations}>Open Integrations</Button> : undefined}
            />
        );
    } else if (result?.state === 'OK') {
        body = (
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Text type="secondary">
                    Suggestions only{result.cached ? ' (cached result)' : ''}. Nothing is saved until you apply a field; applied fields go to review.
                </Text>
                {result.suggestions.map((s) => {
                    const label = fieldLabels?.[s.field];
                    const current = label ? currentValue(s.field) : '';
                    const isHeadcount = s.field === 'companyHeadcount';
                    const band = isHeadcount ? parseBand(s.value) : null;
                    const exact = band ? parseExact(current) : null;
                    const exactInBand = exact !== null && exact >= band.lo && exact <= band.hi;
                    const exactOutsideBand = exact !== null && !exactInBand;
                    const same = (current && String(current).trim() === s.value) || exactInBand;
                    return (
                        <div key={s.field} style={{ border: '1px solid rgba(128,128,128,0.25)', borderRadius: 8, padding: 12 }}>
                            <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                                <div style={{ maxWidth: 560 }}>
                                    <Text strong>{label || titleCaseField(s.field)}</Text>{' '}
                                    <Tag>{s.source.label}</Tag>
                                    <div style={{ whiteSpace: 'pre-wrap' }}>{band ? `Range: ${s.value}` : s.value}</div>
                                    {current && (!same || exactInBand) ? (
                                        <div><Text type="secondary">{exact !== null ? 'Current (exact): ' : 'Current: '}{exact !== null ? exact.toLocaleString('en-IN') : String(current)}</Text></div>
                                    ) : null}
                                    {exactOutsideBand ? <div><Text type="warning">The current exact count is outside this range; applying replaces it with the range.</Text></div> : null}
                                </div>
                                {!label ? (
                                    <Text type="secondary">No matching profile field</Text>
                                ) : applied[s.field] ? (
                                    <Tag color="green">Applied - pending review</Tag>
                                ) : same ? (
                                    <Tag>{exactInBand ? 'Already set (exact count is within range)' : 'Already set'}</Tag>
                                ) : (
                                    <Button size="small" type="primary" disabled={!canApply} onClick={() => apply(s)}>Apply</Button>
                                )}
                            </Space>
                        </div>
                    );
                })}
            </Space>
        );
    }

    return (
        <Card size="small" title={`Auto-fetch company data (${provider.spec.displayName})`} style={{ marginBottom: 16 }}>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Space wrap>
                    <Input placeholder="Website domain (e.g. example.com)" value={domain} onChange={(e) => setDomain(e.target.value)} style={{ minWidth: 260 }} />
                    <Input placeholder="Company name" value={name} onChange={(e) => setName(e.target.value)} style={{ minWidth: 260 }} />
                    <Button type="primary" icon={<SearchOutlined />} loading={busy} onClick={lookup}>Look up</Button>
                </Space>
                {!canApply && <Text type="secondary">Profile edits are locked in the current review state; suggestions cannot be applied.</Text>}
                {body}
            </Space>
        </Card>
    );
};

export default CompanyEnrichmentPanel;
